import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { DialogData } from '../../../../shared/models/dialog-data.model';
import { MatRadioModule } from '@angular/material/radio';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { XDNFeatures } from '../../../../shared/models/cdn-vhost.models';
import { combineLatest, map, startWith } from 'rxjs';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-cdn-features-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
    MatRadioModule,
    MatTabsModule,
    MatSlideToggleModule,
    AsyncPipe,
  ],
  templateUrl: './cdn-features-dialog.component.html',
  styleUrl: './cdn-features-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CdnFeaturesDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<CdnFeaturesDialogComponent>);

  readonly data = inject<DialogData & { features: XDNFeatures }>(MAT_DIALOG_DATA);

  private fb = inject(FormBuilder);

  readonly ERROR_MESSAGES: Record<string, Record<string, string>> = {
    countryBan: {
      invalidAlpha3Codes: 'Invalid Alpha 3 code(s)',
    },
  };

  form = this.fb.nonNullable.group({
    captcha: [false, [Validators.required]],
    captchaLevel: [false, [Validators.required]],
    qsIgnore: [false, [Validators.required]],
    cacheBypass: [false, [Validators.required]],
    cacheReferer: [[] as string[]],
    uaList: [[] as string[]],
    countryBan: [[] as string[], [this.alpha3CodeValidator]],
    countryAction: [''],
    countryHtAccess: [false, [Validators.required]],
  });

  countryListHint$ = combineLatest({
    countryList: this.form.controls.countryBan.valueChanges,
    countryAction: this.form.controls.countryAction.valueChanges.pipe(startWith('')),
  }).pipe(
    map(({ countryList, countryAction }) =>
      countryList.length
        ? `${countryList.length} ${countryList.length === 1 ? 'country is' : 'countries are'} ${countryAction ? (countryAction === 'ban' ? 'banned' : 'allowed') : 'selected'}`
        : '',
    ),
  );

  ngOnInit(): void {
    if (this.data.features) {
      const {
        captcha,
        captchaLevel,
        qsIgnore,
        cacheBypass,
        cacheReferer,
        uaList,
        countryBan,
        countryAction,
        countryHtAccess,
      } = this.data.features;

      const controls = this.form.controls;

      controls.captcha.setValue(captcha);
      controls.captchaLevel.setValue(captchaLevel);
      controls.qsIgnore.setValue(qsIgnore);
      controls.cacheBypass.setValue(cacheBypass);
      if (cacheReferer.length > 0 && cacheReferer[0]) controls.cacheReferer.setValue(cacheReferer);
      if (uaList.length > 0 && uaList[0]) controls.uaList.setValue(uaList);
      if (countryBan.length > 0 && countryBan[0]) controls.countryBan.setValue(countryBan);
      controls.countryAction.setValue(countryAction);
      controls.countryHtAccess.setValue(countryHtAccess);
    }
  }

  setCountryAction(action: 'ban' | 'allow'): void {
    const control = this.form.controls.countryAction;
    if (control.value === action) control.reset('');
    else control.setValue(action);
  }

  submit(): void {
    if (this.form.invalid) return;
    this.dialogRef.close(this.form.getRawValue());
  }

  close(): void {
    this.dialogRef.close();
  }

  private alpha3CodeValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!Array.isArray(value)) {
      return null;
    }

    const invalidValues = value.filter((item: string) => !/^[A-Z]{3}$/.test(item));

    return invalidValues.length ? { invalidAlpha3Codes: invalidValues } : null;
  }
}
